import { doc, getDoc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { getDbSafe } from './firebaseConfig';

/**
 * Activity status: when someone was last active on AfroYap.
 *
 * Heartbeat + last-write on status/{uid}, not a real-time presence system.
 * There is no onDisconnect in Firestore, so nothing here stores "is online":
 * "Active now" is always derived from last_seen at read time, and a client that
 * vanishes without a clean signal simply ages out of the window.
 *
 * Privacy lives in firestore.rules, not here. A status document is readable only
 * when the owner has activity status on, the reader has it on too (the
 * reciprocal hide), and neither has blocked the other. The client checks the
 * same things first only to avoid reads that are bound to be denied.
 */

/** "Active now" lasts this long after the last heartbeat. */
export const ACTIVE_NOW_WINDOW_MS = 2 * 60 * 1000;
/** "Recently active" badge on Discover cards: active within this window. */
export const RECENTLY_ACTIVE_WINDOW_MS = 48 * 60 * 60 * 1000;
/** How often an open, foregrounded app refreshes last_seen. */
export const HEARTBEAT_MS = 60 * 1000;
/** List surfaces share one read per person for this long. */
const CACHE_TTL_MS = 60 * 1000;

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

type CacheEntry = { lastActiveMs: number | null; fetchedAt: number };
const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<number | null>>();

// The viewer's own setting. Defaults to on, matching the opt-out design, until
// their status document says otherwise.
let myVisible = true;
const visibilityListeners = new Set<(visible: boolean) => void>();
const setMyVisibleLocal = (visible: boolean) => {
  myVisible = visible;
  visibilityListeners.forEach(fn => fn(visible));
};

function toMillis(ts: any): number | null {
  if (!ts) return null;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  const ms = new Date(ts).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * The display bucket. Relative only, never a timestamp.
 *
 * Older than a week shows nothing: "Active 3 months ago" is the opposite of the
 * social proof this exists for.
 */
export function activityLabel(lastActiveMs: number | null, now = Date.now()): string | null {
  if (lastActiveMs == null) return null;
  // A clock slightly ahead of the server's makes a fresh heartbeat look like the
  // future. That is still "now".
  const age = Math.max(0, now - lastActiveMs);
  if (age < ACTIVE_NOW_WINDOW_MS) return 'Active now';
  if (age < HOUR_MS) return `Active ${Math.floor(age / 60000)}m ago`;
  if (age < DAY_MS) return 'Active today';
  if (age < WEEK_MS) return 'Active this week';
  return null;
}

export const PresenceService = {
  /** Foreground and every HEARTBEAT_MS while open. */
  async heartbeat(uid: string): Promise<void> {
    try {
      const db = await getDbSafe();
      await setDoc(doc(db, 'status', uid), {
        state: 'online',
        last_seen: serverTimestamp(),
      }, { merge: true });
    } catch (error) {
      console.warn('[PresenceService] heartbeat failed:', error);
    }
  },

  /** Best effort on close. Display ignores `state`; last_seen still ages out. */
  async markOffline(uid: string): Promise<void> {
    try {
      const db = await getDbSafe();
      await setDoc(doc(db, 'status', uid), {
        state: 'offline',
        last_seen: serverTimestamp(),
      }, { merge: true });
    } catch { /* closing anyway */ }
  },

  /** Read the signed-in user's own setting once, at sign-in. */
  async loadMyVisibility(uid: string): Promise<void> {
    try {
      const db = await getDbSafe();
      const snap = await getDoc(doc(db, 'status', uid));
      setMyVisibleLocal(snap.exists() ? snap.data().showActivity !== false : true);
    } catch (error) {
      console.warn('[PresenceService] loadMyVisibility failed:', error);
    }
  },

  getMyVisibility: (): boolean => myVisible,

  subscribeMyVisibility(fn: (visible: boolean) => void): () => void {
    visibilityListeners.add(fn);
    return () => { visibilityListeners.delete(fn); };
  },

  /**
   * The "Show my activity status" toggle. Turning it off also hides everyone
   * else's from this user; the rules enforce that side by reading this field.
   */
  async setMyVisibility(uid: string, visible: boolean): Promise<void> {
    const previous = myVisible;
    setMyVisibleLocal(visible);
    // Whatever was cached was fetched under the old setting.
    cache.clear();
    try {
      const db = await getDbSafe();
      await setDoc(doc(db, 'status', uid), { showActivity: visible }, { merge: true });
    } catch (error) {
      setMyVisibleLocal(previous);
      throw error;
    }
  },

  /** One-shot read with a short shared cache, for list surfaces. Denied reads resolve to null. */
  async getLastActive(uid: string): Promise<number | null> {
    const hit = cache.get(uid);
    if (hit && Date.now() - hit.fetchedAt < CACHE_TTL_MS) return hit.lastActiveMs;
    const pending = inflight.get(uid);
    if (pending) return pending;

    const request = (async () => {
      let lastActiveMs: number | null = null;
      try {
        const db = await getDbSafe();
        const snap = await getDoc(doc(db, 'status', uid));
        lastActiveMs = snap.exists() ? toMillis(snap.data().last_seen) : null;
      } catch {
        // permission-denied is the normal answer for a hidden or blocked user
        lastActiveMs = null;
      }
      cache.set(uid, { lastActiveMs, fetchedAt: Date.now() });
      inflight.delete(uid);
      return lastActiveMs;
    })();
    inflight.set(uid, request);
    return request;
  },

  /** Live read, for the one person on screen (a profile or an open DM). */
  watchLastActive(uid: string, onChange: (lastActiveMs: number | null) => void): () => void {
    let unsub: (() => void) | null = null;
    let cancelled = false;
    (async () => {
      try {
        const db = await getDbSafe();
        if (cancelled) return;
        unsub = onSnapshot(
          doc(db, 'status', uid),
          snap => {
            const lastActiveMs = snap.exists() ? toMillis(snap.data().last_seen) : null;
            cache.set(uid, { lastActiveMs, fetchedAt: Date.now() });
            onChange(lastActiveMs);
          },
          () => onChange(null),
        );
      } catch {
        onChange(null);
      }
    })();
    return () => { cancelled = true; unsub?.(); };
  },

  /** Sign-out: nothing fetched for one account should leak into the next. */
  reset(): void {
    cache.clear();
    inflight.clear();
    setMyVisibleLocal(true);
  },
};
